import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Lock, Zap, CreditCard, AlertTriangle } from 'lucide-react';
import { useBilling, type BillingGate } from '../../stores/billingStore';

// Monetization 360 — the single global modal that fronts every backend spend /
// feature gate (402). Mounted once at the app root; driven entirely by the
// billingStore, which the apiClient interceptor fills on any 402. The CTA
// routes to the org billing page (full-page nav, so it works regardless of
// where in the router tree the 402 fired).

/** `t` passed in explicitly — this is a plain helper, not a component. */
function copyFor(gate: BillingGate, t: (key: string, opts?: Record<string, unknown>) => string): { title: string; body: string; cta: string; icon: ReactNode } {
  switch (gate.code) {
    case 'subscription_required':
      return {
        title: t('upgradeModal.gates.subscriptionRequired.title'),
        body: gate.message || t('upgradeModal.gates.subscriptionRequired.body'),
        cta: t('upgradeModal.gates.subscriptionRequired.cta'),
        icon: <Lock className="w-5 h-5" aria-hidden />,
      };
    case 'feature_not_in_plan':
      return {
        title: t('upgradeModal.gates.featureNotInPlan.title'),
        body: gate.feature
          ? t('upgradeModal.gates.featureNotInPlan.bodyWithFeature', { feature: prettyFeature(gate.feature) })
          : (gate.message || t('upgradeModal.gates.featureNotInPlan.bodyFallback')),
        cta: gate.required_plan ? t('upgradeModal.gates.featureNotInPlan.ctaUpgradeTo', { plan: cap(gate.required_plan) }) : t('upgradeModal.gates.featureNotInPlan.cta'),
        icon: <Zap className="w-5 h-5" aria-hidden />,
      };
    case 'cost_limit_exceeded':
      return {
        title: t('upgradeModal.gates.costLimitExceeded.title'),
        body: gate.message || t('upgradeModal.gates.costLimitExceeded.body'),
        cta: t('upgradeModal.gates.costLimitExceeded.cta'),
        icon: <AlertTriangle className="w-5 h-5" aria-hidden />,
      };
    case 'token_limit_exceeded':
      return {
        title: t('upgradeModal.gates.tokenLimitExceeded.title'),
        body: gate.message || t('upgradeModal.gates.tokenLimitExceeded.body'),
        cta: t('upgradeModal.gates.tokenLimitExceeded.cta'),
        icon: <AlertTriangle className="w-5 h-5" aria-hidden />,
      };
    // TUFG seat scaling — seat-tier gates.
    case 'creator_seat_required':
      return {
        title: t('upgradeModal.gates.creatorSeatRequired.title'),
        body: gate.message || t('upgradeModal.gates.creatorSeatRequired.body'),
        cta: t('upgradeModal.gates.creatorSeatRequired.cta'),
        icon: <Lock className="w-5 h-5" aria-hidden />,
      };
    case 'seat_capability_required':
      return {
        title: t('upgradeModal.gates.seatCapabilityRequired.title'),
        body: gate.message || t('upgradeModal.gates.seatCapabilityRequired.body'),
        cta: t('upgradeModal.gates.seatCapabilityRequired.cta'),
        icon: <Lock className="w-5 h-5" aria-hidden />,
      };
    case 'seat_limit_reached':
      return {
        title: t('upgradeModal.gates.seatLimitReached.title'),
        body: gate.message
          || (gate.seat_type
            ? t('upgradeModal.gates.seatLimitReached.bodyWithType', {
              seatType: gate.seat_type,
              usage: gate.limit != null ? t('upgradeModal.gates.seatLimitReached.usageSuffix', { current: gate.current, limit: gate.limit }) : '',
            })
            : t('upgradeModal.gates.seatLimitReached.bodyFallback')),
        cta: t('upgradeModal.gates.seatLimitReached.cta'),
        icon: <CreditCard className="w-5 h-5" aria-hidden />,
      };
    default:
      return {
        title: t('upgradeModal.gates.default.title'),
        body: gate.message || t('upgradeModal.gates.default.body'),
        cta: t('upgradeModal.gates.default.cta'),
        icon: <CreditCard className="w-5 h-5" aria-hidden />,
      };
  }
}

function prettyFeature(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function cap(s: string): string { return s.charAt(0).toUpperCase() + s.slice(1); }

export function UpgradeModal() {
  const { t } = useTranslation('billing');
  const gate = useBilling((s) => s.gate);
  const clear = useBilling((s) => s.clear);

  useEffect(() => {
    if (!gate) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') clear(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gate, clear]);

  if (!gate) return null;
  const { title, body, cta, icon } = copyFor(gate, t);

  const goToBilling = () => { clear(); window.location.href = '/admin/billing'; };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'var(--ws-overlay)' }}
      onClick={clear}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="ws-tile w-full max-w-md flex flex-col gap-4 p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="flex items-center justify-center w-10 h-10 rounded-full bg-ws-accent-quiet text-ws-accent-ink shrink-0">
              {icon}
            </span>
            <h2 className="font-semibold text-base tracking-tight">{title}</h2>
          </div>
          <button type="button" onClick={clear} className="ws-btn-ghost" title={t('upgradeModal.closeTitle')} aria-label={t('upgradeModal.closeAriaLabel')}>
            <X size={16} />
          </button>
        </header>

        <p className="text-sm text-ws-ink-3 leading-relaxed">{body}</p>

        {/* Usage detail line when the backend provided numbers. */}
        {gate.code === 'cost_limit_exceeded' && gate.cap_usd != null && (
          <p className="text-xs text-ws-ink-4">
            {t('upgradeModal.gates.costLimitExceeded.usageLine', { used: `$${Number(gate.used_usd ?? 0).toFixed(2)}`, cap: `$${Number(gate.cap_usd).toFixed(2)}` })}
          </p>
        )}
        {gate.code === 'token_limit_exceeded' && gate.cap_tokens != null && (
          <p className="text-xs text-ws-ink-4">
            {t('upgradeModal.gates.tokenLimitExceeded.usageLine', { used: fmt(gate.used_tokens), cap: fmt(gate.cap_tokens) })}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={clear} className="ws-btn-ghost ws-btn-sm">{t('upgradeModal.notNow')}</button>
          <button type="button" onClick={goToBilling} className="ws-btn-primary ws-btn-sm inline-flex items-center gap-1.5">
            <CreditCard size={14} /> {cta}
          </button>
        </div>
      </div>
    </div>
  );
}

function fmt(n?: number): string {
  if (n == null) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}
